import { format, parseISO, subDays } from "date-fns";

import type { ScoresEveryDayBrand, ScoresEveryDayBrandRangeValues } from "../../../types/scores";
import type { Brand7dSummary } from "../types";
import { ScoreDailyAverageResponse } from "../response.types";

/**
 * Maps raw `brands_score_every_day` rows into chart state + per-day range extrema.
 */
export function reduceBrandEveryDayRowsToSeries(
  rows: ScoreDailyAverageResponse[],
): {
  scoresByBrand: ScoresEveryDayBrand;
  rangeValues: ScoresEveryDayBrandRangeValues;
} {
  const rangeValues: ScoresEveryDayBrandRangeValues = {};

  const scoresByBrand = rows.reduce(
    (acc: ScoresEveryDayBrand, score: ScoreDailyAverageResponse) => {
      const brandId = score.brand_id;
      if (!acc[brandId]) acc[brandId] = { scores: [] };

      const visibility = Math.round(score.avg_visibility_score);
      const sentiment = Math.round(score.avg_sentiment_score);
      const ranking = score.avg_position;
      const date = score.date;

      if (!rangeValues[date]) {
        rangeValues[date] = {
          visibility: { min: Infinity, max: -Infinity },
          segment: { min: Infinity, max: -Infinity },
        };
      }

      rangeValues[date].visibility.min = Math.min(
        rangeValues[date].visibility.min,
        visibility,
      );
      rangeValues[date].visibility.max = Math.max(
        rangeValues[date].visibility.max,
        visibility,
      );
      rangeValues[date].segment.min = Math.min(
        rangeValues[date].segment.min,
        sentiment,
      );
      rangeValues[date].segment.max = Math.max(
        rangeValues[date].segment.max,
        sentiment,
      );

      acc[brandId].scores.push({
        created_at: score.date,
        visibility,
        sentiment,
        ranking,
      });

      return acc;
    },
    {},
  );

  return { scoresByBrand, rangeValues };
}

/**
 * Means for visibility, sentiment, and position over the last 7 calendar days vs the 7 days before.
 * Pass 14 days of rows from the API. Range end is the latest `date` in `rows`.
 */
export function aggregateBrandEveryDaySummaryByBrand(
  rows: ScoreDailyAverageResponse[],
): Record<number, Brand7dSummary> {
  if (rows.length === 0) return {};

  type Acc = { visSum: number; sentSum: number; posSum: number; count: number };
  const empty = (): Acc => ({ visSum: 0, sentSum: 0, posSum: 0, count: 0 });
  const means = (a: Acc) => ({
    visibility: Math.round(a.visSum / a.count),
    sentiment: Math.round(a.sentSum / a.count),
    position: Math.round(a.posSum / a.count),
  });

  const end = parseISO(rows.reduce((m, r) => (r.date > m ? r.date : m), rows[0].date));
  const ymd = (daysBeforeEnd: number) => format(subDays(end, daysBeforeEnd), "yyyy-MM-dd");
  const curLo = ymd(6);
  const curHi = ymd(0);
  const prevLo = ymd(13);
  const prevHi = ymd(7);

  const byBrand = new Map<number, { current: Acc; prev: Acc }>();

  for (const { brand_id, date, avg_visibility_score, avg_sentiment_score, avg_position } of rows) {
    let r = byBrand.get(brand_id);
    if (!r) {
      r = { current: empty(), prev: empty() };
      byBrand.set(brand_id, r);
    }
    let acc: Acc | null = null;
    if (date >= curLo && date <= curHi) acc = r.current;
    else if (date >= prevLo && date <= prevHi) acc = r.prev;
    if (!acc) continue;
    acc.visSum += avg_visibility_score;
    acc.sentSum += avg_sentiment_score;
    acc.posSum += avg_position;
    acc.count += 1;
  }

  const out: Record<number, Brand7dSummary> = {};
  for (const [brandId, { current, prev }] of byBrand) {
    if (current.count === 0) continue;
    const cur = means(current);
    const prv = prev.count > 0 ? means(prev) : null;
    out[brandId] = {
      brand_id: brandId,
      avg_visibility: cur.visibility,
      avg_sentiment: cur.sentiment,
      avg_position: cur.position,
      week_difference_visibility: prv ? cur.visibility - prv.visibility : 0,
      week_difference_sentiment: prv ? cur.sentiment - prv.sentiment : 0,
      week_difference_position: prv ? cur.position - prv.position : 0,
    };
  }
  return out;
}
